import { MiddlewareAPI } from "@reduxjs/toolkit";
import { HttpStatusCode } from "../constants/httpStatusCode";
import { logout } from "../store/session/slices";
import { ApiError } from "./apiError";
import { getEnvApiUrl, getEnvConfigSelector, getEnvPullWebMediaUrl, getEnvPushWebMediaUrl } from "./env";
import storage, { getSelectedConfig } from "./storage";
import { addToPathNameUrl } from "./utils";
import { OvenMediaContext } from "../interfaces/context";

const getUrl = () => new URL(window.location.href);

const getQueryUrl = (query: string, protocol?: string) => {
  const url = getUrl();
  let queryUrlStr = url.searchParams.get(query);
  if (!queryUrlStr) return undefined;
  if (queryUrlStr.startsWith(':')) {
    queryUrlStr = url.hostname + queryUrlStr;
  }
  if (!queryUrlStr.startsWith('http')) {
    queryUrlStr =  url.protocol + '//' + queryUrlStr;
  }
  const queryUrl = new URL(queryUrlStr);
  if (protocol) queryUrl.protocol = protocol;
  return queryUrl.href;
};

const apiUrlFromQuery = getQueryUrl('apiUrl');
const getQueryApiUrl = (protocol : string) => {
  if (!apiUrlFromQuery) return undefined;
  const url = new URL(apiUrlFromQuery);
  if (protocol) url.protocol = protocol;
  return url.href;
};

const ovenMediaSocketUrlFromQuery = getQueryUrl('ovenmediaSocketUrl');
const getQueryOvenMediaSocketUrl = () => {
  if (!ovenMediaSocketUrlFromQuery) return undefined;
  const url = new URL(ovenMediaSocketUrlFromQuery);
  url.protocol = url.protocol.replace('http', 'ws');
  return url.href;
};

if (!(window as any).__custom_local_data__) (window as any).__custom_local_data__ = {};
(window as any).__custom_local_data__.query = {
  apiUrl: apiUrlFromQuery,
  ovenmediaSocketUrl: ovenMediaSocketUrlFromQuery,
}

export const getConfQueries = () => {
  const queries: {[key: string] : string} = (window as any).__custom_local_data__?.query ?? {};
  return queries;
}
// Source: react-boilerplate => https://github.com/react-boilerplate/react-boilerplate  (app/utils/request.js)

/**
 * Build backend url
 *
 * @returns {string} Returns a url as string
 */
const buildBaseUrl = (protocol : string, baseUrl?: string) => {
  if (baseUrl) {
    const url = new URL(baseUrl);
    url.protocol = protocol;
    return url.href; 
  }
  const url = getUrl();
  const hostNameSplitted = url.hostname.split('.');
  hostNameSplitted.splice(0, 1);
  const hostNameStr = hostNameSplitted.join('.');
  // return 'https://dro-1.dev.kalyzee.com/';
  return `${protocol}//api.${hostNameStr}/`;
};

const getConfigApiUrl = (): string | undefined => {
  if (!getEnvConfigSelector()) return undefined;
  return getSelectedConfig()?.apiUrl;
}
/**
 * Get the current backend base (root) url
 *
 * @returns {string} Returns a url as string
 */
export const getBaseUrl = (baseUrl?: string) => {
  const url = getUrl();
  if (baseUrl) return buildBaseUrl(url.protocol, baseUrl);
  return getConfigApiUrl() ?? getQueryApiUrl(url.protocol) ?? getEnvApiUrl() ?? buildBaseUrl(url.protocol);
};

export const getVideoUrl = () => {
  return addToPathNameUrl(getBaseUrl(), 'videos');
};

const getConfigPullWebMediaUrl = (): string | undefined => {
  if (!getEnvConfigSelector()) return undefined;
  return getSelectedConfig()?.pullWebMediaUrl;
}

const getConfigPushWebMediaUrl = (): string | undefined => {
  if (!getEnvConfigSelector()) return undefined;
  return getSelectedConfig()?.pushWebMediaUrl;
}

export const getOvenMediaPullWebsocketUrl = (context: OvenMediaContext) => {
  let url = getConfigPullWebMediaUrl() ?? getEnvPullWebMediaUrl() ?? context.server.pullWebMediaUrl;
  url = url?.replace('http', 'ws')
  return url;
}

export const getOvenMediaPushWebsocketUrl = (context: OvenMediaContext) => {
  let url = getConfigPushWebMediaUrl() ?? getEnvPushWebMediaUrl() ?? context.server.pushWebMediaUrl;
  url = url?.replace('http', 'ws')
  return url;
}

export const getOvenMediaHlsUrl = (context: OvenMediaContext) => {
  let url = getConfigPullWebMediaUrl() ?? getEnvPullWebMediaUrl() ?? context.server.pullWebMediaUrl;
  return url;
}

export const getOvenMediaOvtUrl = (context: OvenMediaContext) => {
  const pullUrl = getConfigPullWebMediaUrl() ?? getEnvPullWebMediaUrl() ?? context.server.pullWebMediaUrl;
  if (!pullUrl) return undefined;
  let url = new URL(pullUrl);
  url.protocol = 'ovt:'
  return url.href;
}

export const getOvenMediaLLHlsUrl = (context: OvenMediaContext) => {
  let url = getConfigPullWebMediaUrl() ?? getEnvPullWebMediaUrl() ?? context.server.pullWebMediaTlsUrl;
  return url;
}

export const getBullMQAdminInterfaceUrl = (context: OvenMediaContext) => {
  let bullMQJobUrl: string | undefined;
  if (!context) return undefined;
  const apiUrl = getConfigApiUrl() ?? getEnvApiUrl() ?? context?.server.apiUrl;
  if (context.bullMQAdminInterface?.path) {
    const url = new URL(apiUrl);
    url.pathname += context.bullMQAdminInterface.path?.replace("/", "");
    bullMQJobUrl = url.href;
  }
  return bullMQJobUrl;
}

export const getUploadVideoQueueBullMQAdminInterfaceUrl = (context: OvenMediaContext, jobId?: string) => {
  let bullMQJobUrl= getBullMQAdminInterfaceUrl(context);
  if (!bullMQJobUrl) return undefined;
  const url = new URL(bullMQJobUrl);
  url.pathname += `/queue/upload_video/${jobId ?? ''}`;
  return url.href;
}

export const getBuildVideoQueueBullMQAdminInterfaceUrl = (context: OvenMediaContext, jobId?: string) => {
  let bullMQJobUrl= getBullMQAdminInterfaceUrl(context);
  if (!bullMQJobUrl) return undefined;
  const url = new URL(bullMQJobUrl);
  url.pathname += `/queue/build_video/${jobId ?? ''}`;
  return url.href;
}

export const getSubtitleVideoQueueBullMQAdminInterfaceUrl = (context: OvenMediaContext, jobId?: string) => {
  let bullMQJobUrl= getBullMQAdminInterfaceUrl(context);
  if (!bullMQJobUrl) return undefined;
  const url = new URL(bullMQJobUrl);
  url.pathname += `/queue/subtitle_video/${jobId ?? ''}`;
  return url.href;
}

// The transcript beside the recording, served by the middleware's static route — on the record's
// own server, like the Bull Board links. Undefined without a context or a record path.
export const getRecordTranscriptUrl = (context: OvenMediaContext | undefined, record: { path?: string }) => {
  if (!context || !record.path) return undefined;
  const apiUrl = getConfigApiUrl() ?? getEnvApiUrl() ?? context.server.apiUrl;
  if (!apiUrl) return undefined;
  const prefix = context.subtitles?.vodUrlPrefix ?? '/static';
  return addToPathNameUrl(apiUrl, `${prefix}/${record.path}/file.subtitles/transcript.json`.replace(/\/{2,}/g, '/'));
}

const getSocketBaseUrl = (apiUrl?: string) => {
  if (apiUrl) {
    const url = new URL(apiUrl);
    // Keep same security level : http -> ws & https -> wss
    return `${buildBaseUrl(url.protocol.replace('http', 'ws'), apiUrl)}`;
  }
  if (getEnvConfigSelector()) {
    const selectedConfig = getSelectedConfig();
    if (selectedConfig?.apiUrl) {
      const urlObj = new URL(selectedConfig.apiUrl);
      urlObj.protocol = urlObj.protocol.replace('http', 'ws');
      return urlObj.href;
    }
  }
  const url = getUrl();
  const urlFromQuery = getQueryApiUrl(url.protocol.replace('http', 'ws'));
  if (urlFromQuery) return urlFromQuery;
  const envApiUrl = getEnvApiUrl();
  if(envApiUrl) {
    const urlObj = new URL(envApiUrl);
    // Keep same security level : http -> ws & https -> wss
    urlObj.protocol = urlObj.protocol.replace('http', 'ws');
    return urlObj.href;
  }
  // Keep same security level : http -> ws & https -> wss
  return `${buildBaseUrl(url.protocol.replace('http', 'ws'))}`;
};

export const getUserSocketUrl = (apiUrl?: string) => addToPathNameUrl(getSocketBaseUrl(apiUrl), '/admin');

export const getAuthHeaders = (token: string) => ({ Authorization: `Bearer ${token}` });

// TODO : token retrieval
export const getHeaders = (authenticated: boolean) => {
  let headers = {
    'Content-Type': 'application/json',
  };
  if (authenticated) {
    const token = storage.getToken();
    if (token) {
      const authHeaders = getAuthHeaders(token);
      headers = { ...headers, ...authHeaders };
    }
  }
  return headers;
};

export const getInit = (
  method: string,
  body?: object,
  authenticated: boolean = true,
) : RequestInit => {
  const init = {
    method,
    body: JSON.stringify(body as BodyInit),
    headers: getHeaders(authenticated),
  };
  return init;
};

export const request = async (
  store: MiddlewareAPI,
  url: string,
  method: string,
  body?: object,
  authenticated: boolean = true,
  // A 403 can be a business refusal (e.g. editing a revoked API key), not a dead session.
  logoutOnForbidden: boolean = true,
) : Promise<any> => {
  // The query stays out of the pathname: addToPathNameUrl would encode its "?".
  const [path, query] = url.split('?');
  const fullUrl = addToPathNameUrl(getBaseUrl(), path) + (query ? `?${query}` : '');
  const response = await fetch(fullUrl, getInit(method, body, authenticated));
  const data = await response.json();
  if (response.ok) {
    return data;
  }
  if ((logoutOnForbidden && response.status === HttpStatusCode.Forbidden) || response.status === HttpStatusCode.TokenIsExpired) {
    store.dispatch(logout());
  }
  const error: ApiError = new ApiError(data);
  // The api error is described by the JSON body :
  throw error;
};

